<template>
    <common-modal v-if="isOpen" width="large" :closeOnClickAway="closeOnClickAway" @close="emitEvent({ name: 'close' })">
        <common-content-spacer height="medium">
            <common-page-header
                :heading="$t('pages.auth.verify_token.referral_info.heading')"
                :isCenter="false"
                size="minor"
            ></common-page-header>

            <div class="referral-info-content">
                <div class="info-section">
                    <div class="info-row">
                        <div class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.share_id') }}</div>
                            <div class="info-value">
                                {{ $t('pages.auth.verify_token.referral_info.share_id_prefix') }}-{{ referral.id }}
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.sender') }}</div>
                            <div class="info-value">{{ getProviderName }}</div>
                        </div>
                    </div>

                    <div class="info-row">
                        <div class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.timeframe') }}</div>
                            <div class="info-value">
                                {{ referral.referralData.timeframe }}
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.created_at') }}</div>
                            <div class="info-value">{{ getCreatedAt }}</div>
                        </div>
                    </div>

                    <div class="info-row">
                        <div class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.service_requested') }}</div>
                            <div class="info-value info-value--large">{{ referral.referralData.diagnosis }}</div>
                        </div>
                        <div v-if="referral.expiresAt" class="info-item">
                            <div class="info-label">{{ $t('pages.auth.verify_token.referral_info.expires_at') }}</div>
                            <div class="info-value info-value--expires">{{ getExpiresAt }}</div>
                        </div>
                    </div>
                </div>

                <div :class="getDisclaimerSectionClass">
                    <label class="disclaimer-checkbox">
                        <input v-model="disclaimerAccepted" type="checkbox" />
                        <span class="disclaimer-text">{{ $t('pages.referrals.show.disclaimer_modal.content') }}</span>
                    </label>
                </div>

                <div v-if="referral.referralNotes?.length > 0" class="documents-section">
                    <h3 class="section-heading">{{ $t('pages.auth.verify_token.referral_info.clinical_documents') }}</h3>
                    <div class="documents-list">
                        <div v-for="(document, index) in referral.referralNotes" :key="index" class="document-item">
                            <div class="document-info">
                                <common-icon name="file-text" size="medium" class="document-icon"></common-icon>
                                <div class="document-details">
                                    <div class="document-name">{{ document.note }}</div>
                                    <div class="document-meta">
                                        {{ document.extension.toUpperCase() }} · {{ getDocumentSize(document) }}
                                    </div>
                                </div>
                            </div>
                            <div class="document-actions">
                                <common-button
                                    :label="$t('pages.auth.verify_token.referral_info.view')"
                                    type="secondary"
                                    size="small"
                                    @click="viewDocument(document.url)"
                                ></common-button>
                                <common-button
                                    :label="$t('pages.auth.verify_token.referral_info.download')"
                                    type="secondary"
                                    size="small"
                                    @click="downloadDocument(document)"
                                ></common-button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="response-section">
                    <h3 class="section-heading">{{ $t('pages.auth.verify_token.referral_info.response_actions') }}</h3>
                    <div class="response-buttons">
                        <common-button
                            :label="$t('pages.auth.verify_token.referral_info.accept_referral')"
                            type="primary"
                            size="medium"
                            @click="handleAcceptReferral"
                        ></common-button>
                        <common-button
                            :label="$t('pages.auth.verify_token.referral_info.request_more_info')"
                            type="secondary"
                            size="medium"
                            @click="handleRequestMoreInfo"
                        ></common-button>
                        <common-button
                            :label="$t('pages.auth.verify_token.referral_info.decline')"
                            type="secondary"
                            size="medium"
                            @click="handleDecline"
                        ></common-button>
                    </div>
                </div>

                <div class="participation-section">
                    <div class="participation-header">
                        <h3 class="section-heading">{{ $t('pages.auth.verify_token.referral_info.participation_status') }}</h3>
                        <span class="participation-badge">{{ $t('pages.auth.verify_token.referral_info.external') }}</span>
                    </div>
                    <p class="participation-text">
                        {{ $t('pages.auth.verify_token.referral_info.participation_text') }}
                    </p>
                </div>
            </div>

            <common-buttons class="modal-footer-buttons">
                <common-button label="Close" type="primary" size="small" @click="emitEvent({ name: 'close' })"></common-button>
            </common-buttons>
        </common-content-spacer>
    </common-modal>
</template>

<script>
    import FileMixin from 'mixins/FileMixin'
    import DateTimeMixin from 'mixins/DateTimeMixin'
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    import timezone from 'dayjs/plugin/timezone'
    dayjs.extend(utc)
    dayjs.extend(timezone)

    export default {
        mixins: [FileMixin, DateTimeMixin],
        props: {
            isOpen: { type: Boolean, required: true },
            referral: { type: Object, required: false, default: null },
            referralPoolId: { type: String, default: null },
            npiNumber: { type: String, default: null },
            token: { type: String, default: null },
            closeOnClickAway: { type: Boolean, default: true },
        },

        data() {
            return {
                disclaimerAccepted: false,
                isShaking: false,
            }
        },

        computed: {
            getProviderName() {
                if (this.referral.isFromPatient) {
                    return this.referral.referralData?.patient?.firstName + ' ' + this.referral.referralData?.patient?.lastName
                }
                const user = this.referral.fromUser || {}
                const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
                return user.designation ? `${name}, ${user.designation}` : name
            },

            getDisclaimerSectionClass() {
                return {
                    'disclaimer-section': true,
                    'shake-animation': this.isShaking,
                    'disclaimer-section--accepted': this.disclaimerAccepted,
                }
            },

            getExpiresAt() {
                const formatted = dayjs.utc(this.referral.expiresAt).tz(this.getCurrentTimezone()).format('MMMM D, YYYY')

                return `${formatted} at ${dayjs.utc(this.referral.expiresAt).tz(this.getCurrentTimezone()).format('h:mm A')}`
            },

            getCreatedAt() {
                const formatted = dayjs.utc(this.referral.createdAt).tz(this.getCurrentTimezone()).format('MMMM D, YYYY')

                return `${formatted} at ${dayjs.utc(this.referral.createdAt).tz(this.getCurrentTimezone()).format('h:mm A')}`
            },
        },

        methods: {
            triggerShakeAnimation() {
                this.isShaking = true
                setTimeout(() => {
                    this.isShaking = false
                }, 600)
            },

            checkDisclaimerAccepted() {
                if (!this.disclaimerAccepted) {
                    this.triggerShakeAnimation()
                    return false
                }
                return true
            },

            viewDocument(url) {
                if (!this.checkDisclaimerAccepted()) return

                window.open(url, '_blank')
            },

            downloadDocument(document) {
                if (!this.checkDisclaimerAccepted()) return

                this.downloadFile(document.url, document.note)
            },

            handleAcceptReferral() {
                if (!this.checkDisclaimerAccepted()) return

                this.emitEvent({ name: 'close' })

                this.$router.push({
                    name: 'auth.external.propose-time',
                    query: {
                        referralPoolId: this.referralPoolId,
                        npiNumber: this.npiNumber,
                        token: this.token,
                        timeframe: this.referral.referralData.timeframe,
                        createdAt: this.referral.createdAt,
                    },
                })
            },

            handleRequestMoreInfo() {
                if (!this.checkDisclaimerAccepted()) return

                // TODO: Implement request more info logic
            },

            handleDecline() {
                if (!this.checkDisclaimerAccepted()) return

                // TODO: Implement decline referral logic
            },

            getDocumentSize(document) {
                if (document.size >= 1024) {
                    return `${(document.size / 1024).toFixed(2)} MB`
                }

                return `${document.size} KB`
            },
        },
    }
</script>

<style scoped lang="scss">
    .referral-info-content {
        margin-top: 20px;
    }

    .info-section {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 24px;
    }

    .info-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        margin-bottom: 20px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .info-item {
        display: flex;
        flex-direction: column;
    }

    .info-label {
        font-size: 12px;
        color: #6c757d;
        text-transform: uppercase;
        font-weight: 500;
        margin-bottom: 6px;
    }

    .info-value {
        font-size: 16px;
        color: #212529;
        font-weight: 600;

        &--large {
            font-size: 18px;
            font-weight: 700;
        }

        &--expires {
            color: #ff6b35;
        }
    }

    .documents-section,
    .response-section,
    .participation-section {
        margin-bottom: 24px;
    }

    .section-heading {
        font-size: 18px;
        font-weight: 700;
        color: #212529;
        margin-bottom: 16px;
    }

    .documents-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .document-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        background: #ffffff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        transition: box-shadow 0.2s ease;

        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
    }

    .document-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .document-icon {
        color: #6c757d;
    }

    .document-details {
        display: flex;
        flex-direction: column;
    }

    .document-name {
        font-size: 14px;
        font-weight: 600;
        color: #212529;
        margin-bottom: 4px;
    }

    .document-meta {
        font-size: 12px;
        color: #6c757d;
    }

    .document-actions {
        display: flex;
        gap: 8px;
    }

    .response-buttons {
        display: flex;
        gap: 12px;
    }

    .participation-section {
        background: #e7f3ff;
        border-radius: 8px;
        padding: 20px;
    }

    .participation-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
    }

    .participation-badge {
        background: #ffffff;
        color: #0056b3;
        padding: 4px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
    }

    .participation-text {
        font-size: 14px;
        color: #495057;
        margin: 0;
    }

    .disclaimer-section {
        background: #fff9e6;
        border: 2px solid #ffc107;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 24px;
    }

    .disclaimer-section--accepted {
        background: #e6f7ff;
        border: 2px solid #1890ff;
    }

    .disclaimer-checkbox {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        cursor: pointer;
        user-select: none;

        input[type='checkbox'] {
            margin-top: 2px;
            width: 20px;
            height: 20px;
            cursor: pointer;
            flex-shrink: 0;
        }

        .disclaimer-text {
            font-size: 14px;
            line-height: 1.5;
            color: #212529;
            font-weight: 500;
        }
    }

    @keyframes shake {
        0%,
        100% {
            transform: translateX(0);
        }
        10%,
        30%,
        50%,
        70%,
        90% {
            transform: translateX(-8px);
        }
        20%,
        40%,
        60%,
        80% {
            transform: translateX(8px);
        }
    }

    .shake-animation {
        animation: shake 0.6s ease-in-out;
    }

    .modal-footer-buttons {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;
    }

    @media only screen and (max-width: 768px) {
        .info-row {
            grid-template-columns: 1fr;
        }

        .response-buttons {
            flex-direction: column;
        }

        .document-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }

        .document-actions {
            width: 100%;
            justify-content: flex-start;
        }
    }
</style>
